import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  Space,
  Tabs,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { ContainerIcon, FileText, ListCheck, Package } from "lucide-react";
import { FC, useEffect, useState } from "react";
import {
  CompleteServiceTicketModal,
  useCompleteTicket,
} from "~/components/common/complete-ticket";
import { useNotification } from "~/hooks";
import {
  SERVICE_TICKET_KEYS,
  useUpdateServiceTicket,
} from "~/services/service-ticket";
import { TServiceTicket } from "~/types";
import { capitalize } from "~/utils";
import { JobStatus } from "~/utils/constants";
import { InventoryCheckList } from "../inventory/InventoryCheckList";
import { SuppliesCheckList } from "../supplies/SuppliesCheckList";

const { Text, Title } = Typography;
const { TextArea } = Input;

type Props = {
  ticket: TServiceTicket | null;
  refetch: () => void;
  setTicket: (ticket: TServiceTicket | null) => void;
};

interface TicketFormValues {
  status: string;
  notes: string;
  tasksCompleted: Record<string, boolean>;
  inventoryIds?: string[];
}

export const TicketDetails: FC<Props> = ({ ticket, refetch, setTicket }) => {
  const { success, error } = useNotification();
  const [form] = Form.useForm<TicketFormValues>();
  const [activeTab, setActiveTab] = useState("tasks");
  const [taskValues, setTaskValues] = useState<Record<string, boolean>>({});
  const [currentTime, setCurrentTime] = useState(dayjs().format("h:mm A"));
  const { openCompleteTicket } = useCompleteTicket();
  const updateMutation = useUpdateServiceTicket();
  const qc = useQueryClient();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format("h:mm A"));
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Initialize form values when ticket changes
  useEffect(() => {
    if (ticket) {
      const initialTaskValues = {} as Record<string, boolean>;
      ticket.addOnServices?.forEach((task) => {
        initialTaskValues[task.id] = false;
      });

      setTaskValues(initialTaskValues);

      form.setFieldsValue({
        status: "In Progress",
        notes: "",
        tasksCompleted: initialTaskValues,
      });
    }
  }, [ticket, form]);

  // Handle cancelling the service
  const handleCancelService = async (ticketId: string) => {
    try {
      await updateMutation.mutateAsync({
        id: ticketId,
        status: JobStatus.SCHEDULED,
      });
      qc.invalidateQueries({
        queryKey: SERVICE_TICKET_KEYS.GET_MY_SERVICE_TICKETS({}),
      });
      refetch();
      setTicket(null);
      success({ message: "Service ticket cancelled successfully" });
    } catch (err) {
      console.error("Error cancelling service ticket:", err);
      error({ message: "Failed to cancel service ticket" });
    }
  };

  const handleTaskChange = (task: string, checked: boolean) => {
    const updatedTasks = { ...taskValues, [task]: checked };
    setTaskValues(updatedTasks);
    form.setFieldsValue({ tasksCompleted: updatedTasks });
  };

  const handleCompleteService = () => {
    const allTasksCompleted = Object.values(taskValues).every((value) => value === true);

    if (!allTasksCompleted) {
      error({ message: "Please complete all tasks before completing the service." });
      return;
    }

    if (ticket) {
      openCompleteTicket(ticket);
    }
  };

  if (!ticket) {
    return (
      <div className="text-center py-10">
        <Text type="secondary">Select a service ticket to view details</Text>
      </div>
    );
  }

  return (
    <div className="p-4">
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: "In Progress",
          notes: "",
          tasksCompleted: taskValues,
        }}
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1">
            <span className="ant-typography ant-typography-secondary text-sm css-dev-only-do-not-override-1n9b8tj">Status:</span>
            <span className="text-xs w-fit py-1 px-2 rounded-full bg-blue-100 text-blue-800">In progress</span>
          </div>
          <Form.Item
            className="mb-0"
            label={
              <Text type="secondary" className="text-sm">
                Current Time:
              </Text>
            }
          >
            <Input disabled readOnly value={currentTime} />
          </Form.Item>

          <Divider className="my-2 col-span-2" />
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          className="mb-4"
          items={[
            {
              key: "tasks",
              label: (
                <div className="flex items-center">
                  <ListCheck className="size-4 mr-2" />
                  Required Tasks
                </div>
              ),
              children: (
                <div>
                  <Title level={5} className="mb-2">
                    Required Tasks
                  </Title>
                  <div>
                    {ticket.addOnServices?.map((task, index) => (
                      <div key={index} style={{ marginBottom: 8 }}>
                        <Checkbox
                          checked={taskValues[task.id] || false}
                          onChange={(e) =>
                            handleTaskChange(task.id, e.target.checked)
                          }
                        >
                          {capitalize(task.name)}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                </div>
              ),
            },
            {
              key: "inventory",
              label: (
                <div className="flex items-center">
                  <ContainerIcon className="size-4 mr-2" />
                  Inventory
                </div>
              ),
              children: (
                <InventoryCheckList
                  inventories={ticket.inventories || []}
                  serviceTicketId={ticket.id}
                />
              ),
            },
            {
              key: "supplies",
              label: (
                <div className="flex items-center">
                  <Package className="size-4 mr-2" />
                  Supplies
                </div>
              ),
              children: <SuppliesCheckList ticket={ticket} />,
            },
            {
              key: "notes",
              label: (
                <div className="flex items-center">
                  <FileText className="size-4 mr-2" />
                  Service Notes
                </div>
              ),
              children: (
                <div>
                  <Title level={5} className="mb-2">
                    Service Notes
                  </Title>
                  <Form.Item name="notes">
                    <TextArea
                      rows={4}
                      placeholder="Add notes about the service..."
                    />
                  </Form.Item>
                </div>
              ),
            },
          ]}
        />

        <div className="flex justify-between pt-4 border-t mt-4">
          <Button>Take Photos</Button>
          <Space>
            <Button
              type="default"
              onClick={() => handleCancelService(ticket.id)}
              loading={updateMutation.isPending}
            >
              Cancel Service
            </Button>
            <Button
              type="primary"
              className="bg-green-600 hover:bg-green-700"
              onClick={handleCompleteService}
            >
              Complete Service
            </Button>
          </Space>
        </div>
      </Form>
      <CompleteServiceTicketModal
        onSuccess={() => {
          refetch();
          setTicket(null);
        }}
      />
    </div>
  );
};
